import type { RefObject } from "react";
import { LoaderCircle, Radio } from "lucide-react";

import { Badge } from "@workanywhere/ui";
import { Card, CardDescription, CardHeader, CardTitle } from "@workanywhere/ui";
import type { AgentPromptCommandRecord, AgentUpdateRecord } from "../../../../../shared/agent";
import type { AgentUpdateMode, EmptyStateCopy } from "../../agent-updates";
import type { AgentTimelineEntry } from "../../agent-updates";
import { EventRow } from "./event-row";
import { PromptComposer } from "./prompt-composer";

export function EventsTimeline({
  agentApiBaseUrl,
  currentTaskId,
  emptyState,
  entries,
  mode,
  selectedRecord,
  selectedTaskTitle,
  timelineEndRef,
  turnInProgress,
  workspaceKey,
  onPromptSubmitted,
  onSelectRecord,
}: {
  agentApiBaseUrl: string;
  currentTaskId: string | null;
  emptyState: EmptyStateCopy | null;
  entries: AgentTimelineEntry[];
  mode: AgentUpdateMode;
  selectedRecord: AgentUpdateRecord | null;
  selectedTaskTitle: string | null;
  timelineEndRef: RefObject<HTMLDivElement | null>;
  turnInProgress: boolean;
  workspaceKey: string;
  onPromptSubmitted: (command: AgentPromptCommandRecord) => void;
  onSelectRecord: (recordId: string) => void;
}) {
  const rowCount = entries.length + (turnInProgress ? 1 : 0);

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-sm font-semibold">{selectedTaskTitle ?? "Live Events"}</h2>
            <Badge variant="secondary">{rowCount}</Badge>
          </div>
          <Badge variant="outline" className="gap-1 text-muted-foreground">
            <Radio className="size-3" />
            Oldest to newest
          </Badge>
        </div>

        <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
          {emptyState ? (
            <div className="flex h-full min-h-[440px] items-center justify-center p-6">
              <Card className="w-full max-w-md rounded-lg py-5 text-center shadow-none">
                <CardHeader>
                  <CardTitle>{emptyState.title}</CardTitle>
                  <CardDescription>{emptyState.description}</CardDescription>
                </CardHeader>
              </Card>
            </div>
          ) : (
            <ol className="divide-y">
              {entries.map((entry) => (
                <EventRow
                  key={entry.id}
                  entry={entry}
                  selected={entry.kind === "event" && entry.record.id === selectedRecord?.id}
                  canInspect={mode === "developer"}
                  onSelect={() => {
                    if (entry.kind === "event") {
                      onSelectRecord(entry.record.id);
                    }
                  }}
                />
              ))}
              {turnInProgress ? <TurnInProgressRow /> : null}
              <div ref={timelineEndRef} />
            </ol>
          )}
        </div>

        <PromptComposer
          agentApiBaseUrl={agentApiBaseUrl}
          currentTaskId={currentTaskId}
          workspaceKey={workspaceKey}
          onPromptSubmitted={onPromptSubmitted}
        />
      </div>
    </section>
  );
}

function TurnInProgressRow() {
  return (
    <li aria-live="polite">
      <div className="grid w-full grid-cols-[32px_minmax(0,1fr)_88px] gap-3 px-4 py-3 text-left">
        <span className="mt-1 flex size-7 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700">
          <LoaderCircle className="size-4 animate-spin" />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Turn in progress</span>
            <Badge variant="outline">turn.started</Badge>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>Agent is working</span>
            <span className="progress-dot" />
            <span className="progress-dot progress-dot-delay-1" />
            <span className="progress-dot progress-dot-delay-2" />
          </div>
        </div>
        <span className="text-right text-xs text-muted-foreground">now</span>
      </div>
    </li>
  );
}
